import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowRight, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLOR = {
  Critical: "#FF2A2A",
  High:     "#FF8000",
  Moderate: "#FFB000",
  Low:      "#1FDD83",
};

function fmtN(n) {
  if (n == null) return "—";
  if (n >= 10000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

export default function PublicRiskIndex() {
  const [data, setData] = useState(null);
  const [window, setWindow] = useState("last_30d");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/risk-index`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(String(e)); });
    return () => { cancelled = true; };
  }, []);

  if (error) return null;  // fail silent on public landing
  const snap = data?.windows?.[window];

  return (
    <section className="max-w-[1400px] mx-auto px-6 sm:px-8 py-16" data-testid="public-risk-index">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>PUBLIC DATA · PQC RISK INDEX</div>
          <h2 className="qt-h2 text-3xl sm:text-5xl mt-2 max-w-3xl">
            The state of post-quantum risk, measured live.
          </h2>
          <p className="mt-3 max-w-3xl" style={{ color: "var(--qt-text-2)" }}>
            Aggregated, deterministic scoring across every QorTrace scan. Updates every 60&nbsp;seconds — methodology{" "}
            <span className="qt-mono text-xs" style={{ color: "var(--qt-cyan)" }}>{data?.methodology_version || "—"}</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 qt-mono text-[10px]" style={{ letterSpacing: "0.15em" }} data-testid="risk-index-window-toggle">
          {[
            { k: "last_30d", label: "30d" },
            { k: "all_time", label: "ALL TIME" },
          ].map((w) => (
            <button
              key={w.k}
              onClick={() => setWindow(w.k)}
              data-testid={`risk-index-window-${w.k}`}
              className="px-3 py-1.5 rounded transition-colors"
              style={{
                background: window === w.k ? "rgba(0,229,255,0.1)" : "transparent",
                border: `1px solid ${window === w.k ? "rgba(0,229,255,0.4)" : "var(--qt-border)"}`,
                color: window === w.k ? "var(--qt-cyan)" : "var(--qt-text-2)",
                cursor: "pointer",
              }}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {!snap ? (
        <div className="qt-card mt-8" style={{ color: "var(--qt-text-3)" }}>
          <RefreshCw size={14} className="inline -mt-1 mr-2 animate-spin" /> Loading index…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="risk-index-kpis">
            <StatTile
              testid="risk-index-total-scans"
              label="TOTAL SCANS"
              value={fmtN(snap.total_scans)}
              sub={`across ${snap.chains_covered} chain${snap.chains_covered === 1 ? "" : "s"}`}
            />
            <StatTile
              testid="risk-index-unique-addresses"
              label="UNIQUE ADDRESSES"
              value={fmtN(snap.unique_addresses)}
              sub={window === "last_30d" ? "in the last 30 days" : "all time"}
            />
            <StatTile
              testid="risk-index-avg-score"
              label="AVG PQC EXPOSURE"
              value={`${snap.avg_score}`}
              sub={`0 = safe · 100 = critical · ${snap.pubkey_exposed_pct}% pubkey exposed`}
              accent={snap.avg_score >= 50 ? "#FF2A2A" : snap.avg_score >= 25 ? "#FFB000" : "#1FDD83"}
            />
            <StatTile
              testid="risk-index-critical-pct"
              label="CRITICAL + HIGH"
              value={`${(snap.tier_pct.Critical + snap.tier_pct.High).toFixed(1)}%`}
              sub={`of scanned addresses`}
              accent="#FF2A2A"
            />
          </div>

          {/* Tier distribution bar */}
          <div className="qt-card mt-6" data-testid="risk-index-tier-bar">
            <div className="flex items-baseline justify-between">
              <div className="qt-label">TIER DISTRIBUTION</div>
              <div className="qt-mono text-[10px]" style={{ color: "var(--qt-text-3)", letterSpacing: "0.15em" }}>
                <Activity size={10} className="inline -mt-0.5 mr-1" /> LIVE
              </div>
            </div>
            <div className="mt-3 h-3 rounded overflow-hidden flex" style={{ background: "rgba(255,255,255,0.04)" }}>
              {["Critical", "High", "Moderate", "Low"].map((t) => {
                const pct = snap.tier_pct[t] || 0;
                return (
                  <div
                    key={t}
                    data-testid={`risk-index-bar-${t.toLowerCase()}`}
                    style={{
                      width: `${pct}%`,
                      background: TIER_COLOR[t],
                      transition: "width 0.4s",
                    }}
                    title={`${t} · ${pct}%`}
                  />
                );
              })}
            </div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {["Critical", "High", "Moderate", "Low"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm">
                  <span style={{
                    display: "inline-block", width: 10, height: 10, borderRadius: 2,
                    background: TIER_COLOR[t],
                  }} />
                  <span className="qt-mono text-xs" style={{ color: "var(--qt-text-3)", letterSpacing: "0.12em" }}>
                    {t.toUpperCase()}
                  </span>
                  <span className="qt-mono text-xs ml-auto" style={{ color: "#fff" }}>
                    {snap.tier_pct[t]}%
                  </span>
                  <span className="qt-mono text-[10px]" style={{ color: "var(--qt-text-3)" }}>
                    ({fmtN(snap.tier_counts[t])})
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 items-center">
            <Link
              to="/scan"
              data-testid="risk-index-scan-cta"
              className="qt-btn qt-btn-primary"
              style={{ fontSize: 12 }}
            >
              Scan your address <ArrowRight size={14} />
            </Link>
            <Link
              to="/methodology"
              data-testid="risk-index-methodology-link"
              className="qt-btn qt-btn-ghost"
              style={{ fontSize: 12, border: "1px solid var(--qt-border)" }}
            >
              How we score
            </Link>
          </div>
        </>
      )}
    </section>
  );
}

function StatTile({ testid, label, value, sub, accent }) {
  return (
    <div className="qt-card" data-testid={testid} style={{ padding: "20px 22px" }}>
      <div className="qt-label">{label}</div>
      <div className="qt-display text-3xl mt-1" style={{ color: accent || "#fff", letterSpacing: "-0.02em" }}>
        {value}
      </div>
      <div className="qt-mono text-[10px] mt-1" style={{ color: "var(--qt-text-3)", letterSpacing: "0.1em" }}>
        {sub}
      </div>
    </div>
  );
}
